/**
 * Custom React Flow node renderer for protocol criteria, atoms, and gates.
 */

import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import type { ProtocolDagNodeData } from "./protocolDagModel";

type ProtocolFlowNode = Node<ProtocolDagNodeData, "protocolNode">;

const KIND_STYLES: Record<
  ProtocolDagNodeData["kind"],
  { border: string; bg: string; badge: string }
> = {
  criterion: {
    border: "border-slate-400",
    bg: "bg-white",
    badge: "bg-slate-900 text-white",
  },
  atom: {
    border: "border-sky-300",
    bg: "bg-sky-50",
    badge: "bg-sky-700 text-white",
  },
  gate: {
    border: "border-indigo-300",
    bg: "bg-indigo-50",
    badge: "bg-indigo-700 text-white",
  },
};

/**
 * Render one protocol DAG node with source/target handles.
 *
 * @param props - React Flow node props
 * @returns Node element
 */
export function ProtocolDagNode({
  data,
  selected,
}: NodeProps<ProtocolFlowNode>) {
  const style = KIND_STYLES[data.kind];
  return (
    <div
      className={`w-[220px] rounded-lg border-2 px-3 py-2 shadow-sm ${style.border} ${style.bg} ${
        selected ? "ring-2 ring-amber-400" : ""
      } ${data.expanded ? "border-slate-900" : ""}`}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!h-2.5 !w-2.5 !border-slate-500 !bg-white"
      />
      <div className="flex items-center justify-between gap-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${style.badge}`}
        >
          {data.kind}
        </span>
        <div className="flex gap-1">
          {data.hardStop ? (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
              hard-stop
            </span>
          ) : null}
          {data.needsReview ? (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              review
            </span>
          ) : null}
        </div>
      </div>
      <div className="mt-1 truncate text-sm font-semibold text-slate-900">
        {data.label}
      </div>
      <div className="mt-0.5 line-clamp-2 text-xs text-slate-600">
        {data.subtitle}
      </div>
      {data.kind === "criterion" ? (
        <div className="mt-1 text-[10px] text-slate-500">
          {data.expanded ? "Double-click to collapse" : "Double-click to expand"}
        </div>
      ) : null}
      <Handle
        type="source"
        position={Position.Right}
        className="!h-2.5 !w-2.5 !border-slate-500 !bg-white"
      />
    </div>
  );
}
